import { Popover, PopoverContent, PopoverTrigger } from "@nimit9/signet-ui/components/popover"
import { BellIcon } from "lucide-react"
import { useState } from "react"
import {
  type Alert,
  useAlerts,
  useDismissAlert,
  useGenerateAlerts,
  useMarkAllRead,
} from "@/hooks/use-alerts"
import { useDataLocked } from "@/hooks/use-veiled"
import { cn } from "@/lib/cn"

const TYPE_ICONS: Record<string, string> = {
  budget_exceeded: "💸",
  unusual_spend: "📈",
  emi_ending: "🎉",
  insurance_renewal: "🛡️",
  sip_due: "📅",
  goal_milestone: "🎯",
  bill_due: "📋",
  net_worth_milestone: "💰",
}

function timeAgo(dateStr: string): string {
  const diff = Date.now() - new Date(dateStr).getTime()
  const mins = Math.floor(diff / 60_000)
  if (mins < 1) return "just now"
  if (mins < 60) return `${mins}m ago`
  const hrs = Math.floor(mins / 60)
  if (hrs < 24) return `${hrs}h ago`
  const days = Math.floor(hrs / 24)
  return `${days}d ago`
}

function AlertItem({ alert, onDismiss }: { alert: Alert; onDismiss: (id: string) => void }) {
  return (
    <div
      className={cn(
        "flex gap-2.5 px-3 py-2.5 transition-colors hover:bg-bg-elevated",
        !alert.isRead && "bg-accent-green/5",
      )}
    >
      <span className="mt-0.5 text-[14px]">{TYPE_ICONS[alert.type] ?? "🔔"}</span>
      <div className="min-w-0 flex-1">
        <p
          className={cn(
            "text-[12px] leading-tight",
            alert.isRead ? "text-text-secondary" : "font-medium text-text-primary",
          )}
        >
          {alert.title}
        </p>
        <p className="mt-0.5 text-[11px] leading-tight text-text-muted">{alert.message}</p>
        <p className="mt-1 text-[10px] text-text-hint">{timeAgo(alert.createdAt)}</p>
      </div>
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation()
          onDismiss(alert.id)
        }}
        className="shrink-0 self-start text-[11px] text-text-hint hover:text-text-muted"
        title="Dismiss"
        aria-label="Dismiss alert"
      >
        ×
      </button>
    </div>
  )
}

/**
 * The alerts bell. While user data is locked (private mode veiled, or the
 * mode isn't known yet) it renders a static, disabled bell: no alerts query,
 * no unread count — nothing from the account reaches the DOM or the cache.
 */
export function AlertBell() {
  const locked = useDataLocked()
  if (locked) {
    return (
      <span
        data-testid="alert-bell-locked"
        className="flex h-7 w-7 items-center justify-center rounded-lg text-text-muted"
      >
        <BellIcon className="size-4" aria-hidden="true" />
      </span>
    )
  }
  return <AlertBellLive />
}

function AlertBellLive() {
  const { data } = useAlerts()
  const dismiss = useDismissAlert()
  const markAllRead = useMarkAllRead()
  const generate = useGenerateAlerts()
  const [open, setOpen] = useState(false)

  const unread = data?.unreadCount ?? 0
  const alerts = data?.data ?? []

  return (
    <Popover
      open={open}
      onOpenChange={(o) => {
        setOpen(o)
        if (o && unread > 0) markAllRead.mutate()
      }}
    >
      <PopoverTrigger asChild>
        <button
          type="button"
          className="relative flex h-7 w-7 items-center justify-center rounded-lg text-text-secondary hover:bg-bg-elevated hover:text-text-primary"
          title="Alerts"
        >
          <svg
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            role="img"
            aria-label="Alerts"
          >
            <path d="M6 8a6 6 0 0 1 12 0c0 7 3 9 3 9H3s3-2 3-9" />
            <path d="M10.3 21a1.94 1.94 0 0 0 3.4 0" />
          </svg>
          {unread > 0 && (
            <span
              role="status"
              aria-label={`${unread} unread alerts`}
              className="absolute -right-1 -top-1 flex h-[18px] min-w-[18px] items-center justify-center rounded-full bg-accent-red px-1 text-label font-semibold leading-none text-accent-red-fg shadow-sm"
            >
              {unread > 9 ? "9+" : unread}
            </span>
          )}
        </button>
      </PopoverTrigger>

      <PopoverContent
        side="top"
        align="start"
        sideOffset={8}
        className="w-[320px] overflow-hidden rounded-xl p-0"
      >
        <div className="flex items-center justify-between border-b border-border-default px-3 py-2">
          <p className="text-[12px] font-medium text-text-primary">Alerts</p>
          <button
            type="button"
            onClick={() => generate.mutate()}
            disabled={generate.isPending}
            className="text-[11px] text-accent-green hover:underline disabled:opacity-50"
          >
            {generate.isPending ? "Checking..." : "Refresh"}
          </button>
        </div>

        <div className="max-h-[360px] overflow-y-auto divide-y divide-border-default">
          {alerts.length === 0 ? (
            <div className="px-3 py-8 text-center">
              <p className="text-[12px] text-text-muted">No alerts</p>
              <p className="mt-1 text-[11px] text-text-hint">
                Hit Refresh to check for budget overruns, spending spikes, and more
              </p>
            </div>
          ) : (
            alerts.map((alert) => (
              <AlertItem key={alert.id} alert={alert} onDismiss={(id) => dismiss.mutate(id)} />
            ))
          )}
        </div>
      </PopoverContent>
    </Popover>
  )
}
